import React from "react";
import { TopBar } from "@/components/TopBar";
import { apiGetMonitoring, apiListPos, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, Search, Settings2, Tv } from "lucide-react";
import * as W from "@/lib/waktu";
import { namaPos, petaPos as buatPetaPos } from "@/lib/posNama";
import RekapPcs from "@/components/RekapPcs";
import { nomorFaktur, tertahanTeks } from "@/lib/format";

const OVERALL_STYLE = {
  "Menunggu Produksi": "border-slate-600 bg-slate-800/40 text-slate-300",
  "Sedang Diproses": "border-amber-500 bg-amber-500 text-slate-950 font-semibold",
  "Pemeriksaan QC": "border-cyan-500/40 bg-cyan-500/10 text-cyan-300",
  "Perlu Perbaikan": "border-red-500/40 bg-red-500/10 text-red-300",
  Selesai: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  Dibatalkan: "border-red-500/40 bg-red-500/10 text-red-300",
};

const STEP_STYLE = {
  PENDING: "border-slate-600 bg-slate-800/40 text-slate-400",
  IN_PROGRESS: "border-amber-500 bg-amber-500 text-slate-950 font-semibold",
  COMPLETED: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
};

const OVERALL_OPTIONS = [
  "Menunggu Produksi",
  "Sedang Diproses",
  "Pemeriksaan QC",
  "Perlu Perbaikan",
  "Selesai",
  "Dibatalkan",
];
const KATEGORI_OPTIONS = ["BANNER", "DTF", "JERSEY", "CUSTOM"];

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return W.waktu(iso);
  } catch {
    return iso;
  }
};

export default function ProductionMonitoring() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [posList, setPosList] = React.useState([]);

  const [status, setStatus] = React.useState("");
  const [kategori, setKategori] = React.useState("");
  const [posCode, setPosCode] = React.useState("");
  const [qSpk, setQSpk] = React.useState("");
  const [qOrder, setQOrder] = React.useState("");
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");
  const [tertahan, setTertahan] = React.useState(false);  // owner 2026-10-12: only SPKs not in production yet
  const [tab, setTab] = React.useState("monitoring");  // v2.24: "Rekap pcs" per station (Owner & Manager)
  const petaPos = React.useMemo(() => buatPetaPos(posList), [posList]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (status) params.status = status;
      if (kategori) params.kategori = kategori;
      if (posCode) params.pos_code = posCode;
      if (qSpk.trim()) params.q_spk = qSpk.trim();
      if (qOrder.trim()) params.q_order = qOrder.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (tertahan) params.tertahan = true;
      setRows(await apiGetMonitoring(params));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [status, kategori, posCode, qSpk, qOrder, dateFrom, dateTo, tertahan]);

  React.useEffect(() => {
    load();
  }, [load]);
  React.useEffect(() => {
    apiListPos().then(setPosList).catch(() => {});
  }, []);

  const resetFilters = () => {
    setStatus("");
    setKategori("");
    setPosCode("");
    setQSpk("");
    setQOrder("");
    setDateFrom("");
    setDateTo("");
    setTertahan(false);
  };

  return (
    <div data-testid="monitoring-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">
              Production Monitoring
            </h1>
            <p className="text-sm text-slate-400 mt-1">Kondisi produksi aktual berdasarkan workflow instance. Read-only.</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* owner 2026-10-16 malam: the production screen (TV ruang produksi) is a mode of this menu */}
            <a
              data-testid="monitoring-layar"
              href="/monitoring/layar"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-950 hover:bg-amber-400"
            >
              <Tv size={14} /> Layar produksi
            </a>
            <Link
              data-testid="monitoring-atur-layar"
              to="/production-monitoring/layar"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-slate-300 hover:text-amber-300 hover:border-amber-500/40"
            >
              <Settings2 size={14} /> Atur layar &amp; kode TV
            </Link>
            <button
              data-testid="monitoring-refresh"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-slate-300 hover:text-amber-300 hover:border-amber-500/40"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        <div className="flex gap-2" data-testid="monitoring-tabs">
          {[["monitoring", "Monitoring"], ["rekap", "Rekap pcs"]].map(([k, l]) => (
            <button key={k} data-testid={`monitoring-tab-${k}`} onClick={() => setTab(k)} className={`rounded-md px-3 py-1.5 text-sm border ${tab === k ? "border-amber-500 bg-amber-500 text-slate-950 font-semibold" : "border-slate-700 text-slate-400"}`}>{l}</button>
          ))}
        </div>
        {tab === "rekap" ? <RekapPcs pengawas posList={posList} petaPos={petaPos} /> : (<>
        {error && (
          <div data-testid="monitoring-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 grid gap-3 md:grid-cols-3 lg:grid-cols-4" data-testid="monitoring-filters">
          <select data-testid="monitoring-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full min-w-0 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
            <option value="">Semua Status</option>
            {OVERALL_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="monitoring-filter-kategori" value={kategori} onChange={(e) => setKategori(e.target.value)} className="w-full min-w-0 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
            <option value="">Semua Kategori</option>
            {KATEGORI_OPTIONS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select data-testid="monitoring-filter-pos" value={posCode} onChange={(e) => setPosCode(e.target.value)} className="w-full min-w-0 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
            <option value="">Semua Pos</option>
            {posList.map((p) => <option key={p.code} value={p.code}>{p.nama || namaPos(p.code)}</option>)}
          </select>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="monitoring-search-spk" value={qSpk} onChange={(e) => setQSpk(e.target.value)} placeholder="Cari No. SPK" className="w-full rounded-lg border border-slate-700 bg-slate-950 pl-8 pr-3 py-2 text-sm text-slate-200" />
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="monitoring-search-order" value={qOrder} onChange={(e) => setQOrder(e.target.value)} placeholder="Cari No. Faktur / Order" className="w-full rounded-lg border border-slate-700 bg-slate-950 pl-8 pr-3 py-2 text-sm text-slate-200" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Dari Tanggal</label>
            <input type="date" data-testid="monitoring-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-200" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Sampai Tanggal</label>
            <input type="date" data-testid="monitoring-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-200" />
          </div>
          <label className="inline-flex items-center gap-2 self-end py-2 text-sm text-slate-300"><input data-testid="monitoring-filter-tertahan" type="checkbox" checked={tertahan} onChange={(e) => setTertahan(e.target.checked)} /> Hanya yang tertahan</label>
          <button data-testid="monitoring-reset" onClick={resetFilters} className="rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm text-slate-300 hover:text-amber-300 hover:border-amber-500/40 self-end">
            Reset Filter
          </button>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Memuat…</div>
        ) : (
          <div className="space-y-4" data-testid="monitoring-list">
            {rows.map((r) => (
              <div key={r.spk_id} data-testid={`monitoring-spk-${r.nomor_spk}`} className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-slate-800">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <span className="font-mono text-amber-300">{r.nomor_spk}</span>
                    <span data-testid={`monitoring-faktur-${r.nomor_spk}`} className="font-mono text-slate-400">Faktur: {nomorFaktur(r)}</span>
                    <span className="text-slate-300">{r.customer_nama || "—"}</span>
                    {r.cs_nama && <span data-testid={`monitoring-cs-${r.nomor_spk}`} className="text-xs text-slate-400">CS: {r.cs_nama}</span>}
                    <span className="text-slate-500 text-xs">{fmtTime(r.created_at)}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                  {tertahanTeks(r) && <span data-testid={`monitoring-tertahan-${r.nomor_spk}`} className="text-xs font-semibold px-3 py-1 rounded-md border border-amber-500/50 bg-amber-500/10 text-amber-300">{tertahanTeks(r)}</span>}
                  {!tertahanTeks(r) && <span data-testid={`monitoring-overall-${r.nomor_spk}`} className={`text-xs font-mono px-3 py-1 rounded-md border ${OVERALL_STYLE[r.overall_status] || OVERALL_STYLE["Menunggu Produksi"]}`}>
                    {r.overall_status}
                  </span>}
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  {r.instances.map((inst) => (
                    <div key={inst.instance_id} className="rounded-lg border border-slate-800 bg-slate-950/40 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="font-mono text-amber-300 text-sm">{inst.workflow_code}</span>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-cyan-500/30 bg-cyan-500/10 text-cyan-300">{inst.kategori}</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                              <th className="px-2 py-1.5">Step</th>
                              <th className="px-2 py-1.5">Pos</th>
                              <th className="px-2 py-1.5">Status</th>
                              <th className="px-2 py-1.5">Mulai</th>
                              <th className="px-2 py-1.5">Selesai</th>
                              <th className="px-2 py-1.5">QC</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inst.steps.map((s) => (
                              <tr key={s.urutan} className="border-b border-slate-800/50">
                                <td className="px-2 py-1.5 font-mono text-slate-400">{s.urutan}</td>
                                <td className="px-2 py-1.5 text-slate-200">{s.pos_nama}</td>
                                <td className="px-2 py-1.5"><span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${STEP_STYLE[s.status] || STEP_STYLE.PENDING}`}>{s.status}</span></td>
                                <td className="px-2 py-1.5 text-slate-400 text-xs">{fmtTime(s.started_at)}</td>
                                <td className="px-2 py-1.5 text-slate-400 text-xs">{fmtTime(s.finished_at)}</td>
                                <td className="px-2 py-1.5">
                                  {s.is_qc && s.qc_result ? (
                                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${s.qc_result === "PASSED" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-red-500/40 bg-red-500/10 text-red-300"}`}>
                                      {s.qc_result}
                                    </span>
                                  ) : (
                                    <span className="text-slate-600 text-xs">{s.is_qc ? "—" : ""}</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {rows.length === 0 && (
              <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-10 text-center text-slate-500">
                Tidak ada data produksi yang cocok dengan filter.
              </div>
            )}
          </div>
        )}
        </>)}
      </main>
    </div>
  );
}
